import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Alert,
  Tooltip
} from '@mui/material';

import { SKIP_REASON_LABELS } from '../../../services/w9Service';

const money = (amount) =>
  `$${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

/**
 * Qualifying contractors for a tax year, with per-row selection.
 *
 * Contractors who qualify but have no email are listed separately rather than
 * hidden: they still owe a W-9, so someone has to chase them another way.
 */
const ContractorList = ({
  recipients = [],
  skipped = [],
  selectedEmails,
  onToggle,
  onToggleAll,
  taxYear,
  threshold,
  disabled = false
}) => {
  const allSelected = recipients.length > 0 && selectedEmails.length === recipients.length;
  const someSelected = selectedEmails.length > 0 && !allSelected;
  const selectedTotal = recipients
    .filter((r) => selectedEmails.includes(r.email))
    .reduce((sum, r) => sum + Number(r.amount), 0);

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={onToggleAll}
                  disabled={disabled || recipients.length === 0}
                  inputProps={{ 'aria-label': 'Select all contractors' }}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Contractor</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {taxYear} payments
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recipients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No contractors reached the {money(threshold || 600)} threshold for {taxYear}.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              recipients.map((c) => {
                const checked = selectedEmails.includes(c.email);
                return (
                  <TableRow
                    key={c.email}
                    hover
                    selected={checked}
                    onClick={() => !disabled && onToggle(c.email)}
                    sx={{ cursor: disabled ? 'default' : 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={checked}
                        disabled={disabled}
                        inputProps={{ 'aria-label': `Select ${c.name}` }}
                      />
                    </TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {c.email}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {money(c.amount)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {recipients.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            size="small"
            color={selectedEmails.length ? 'primary' : 'default'}
            label={`${selectedEmails.length} of ${recipients.length} selected`}
          />
          <Tooltip title="Total reportable income across the selected contractors">
            <Chip size="small" variant="outlined" label={`${money(selectedTotal)} selected`} />
          </Tooltip>
        </Box>
      )}

      {skipped.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {skipped.length} contractor{skipped.length === 1 ? '' : 's'} cannot be emailed
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            These still need a W-9 on file — they have to be contacted another way.
          </Typography>
          {skipped.map((s) => (
            <Typography key={`${s.name}-${s.reason}`} variant="body2" component="div">
              • {s.name} — {money(s.amount)} —{' '}
              {SKIP_REASON_LABELS[s.reason] || s.reason}
            </Typography>
          ))}
        </Alert>
      )}
    </Box>
  );
};

export default ContractorList;
